/**
 * Reports Feature - Shared Hooks for Configuration
 *
 * Custom hooks for checking report module configuration and feature availability
 */

import { useMemo } from 'react';
import { useReportTenantConfig } from '../queries';
import type { TenantConfigWithReporting } from '../types';

/**
 * Hook to check if reporting module is enabled
 */
export function useReportFeatureEnabled() {
  const { data: tenantConfig, isLoading, error, refetch } = useReportTenantConfig();

  const isReportingEnabled = useMemo(() => {
    return tenantConfig?.modules?.reporting?.enabled ?? false;
  }, [tenantConfig]);

  const isArExceptionReportingEnabled = useMemo(() => {
    return (
      isReportingEnabled &&
      (tenantConfig?.modules?.reporting?.arExceptionReporting?.enabled ?? false)
    );
  }, [tenantConfig, isReportingEnabled]);

  const isTrustReconciliationEnabled = useMemo(() => {
    return (
      isReportingEnabled &&
      (tenantConfig?.modules?.reporting?.trustReconciliationReporting?.enabled ?? false)
    );
  }, [tenantConfig, isReportingEnabled]);

  const isMatterLedgerReportingEnabled = useMemo(() => {
    return (
      isReportingEnabled &&
      (tenantConfig?.modules?.reporting?.matterLedgerReporting?.enabled ?? false)
    );
  }, [tenantConfig, isReportingEnabled]);

  const isMatterStatementReportingEnabled = useMemo(() => {
    return (
      isReportingEnabled &&
      (tenantConfig?.modules?.reporting?.matterStatementReporting?.enabled ?? false)
    );
  }, [tenantConfig, isReportingEnabled]);

  return {
    tenantConfig: tenantConfig as TenantConfigWithReporting | undefined,
    isLoading,
    error,
    refetch,
    isReportingEnabled,
    isArExceptionReportingEnabled,
    isTrustReconciliationEnabled,
    isMatterLedgerReportingEnabled,
    isMatterStatementReportingEnabled,
  };
}

